class CTFeedbackSpotlight {
  constructor(section) {
    this.section = section;
    this.viewport = section.querySelector(
      '[data-ct-spotlight-viewport]'
    );
    this.slides = Array.from(
      section.querySelectorAll('[data-ct-spotlight-slide]')
    );
    this.captions = Array.from(
      section.querySelectorAll('[data-ct-spotlight-caption]')
    );
    this.previousButton = section.querySelector(
      '[data-ct-spotlight-previous]'
    );
    this.nextButton = section.querySelector(
      '[data-ct-spotlight-next]'
    );
    this.currentCounter = section.querySelector(
      '[data-ct-spotlight-current]'
    );

    this.loop = section.dataset.loop === 'true';
    this.autoplay = section.dataset.autoplay === 'true';
    this.interval =
      Number.parseInt(section.dataset.interval, 10) || 6000;

    this.activeIndex = 0;
    this.scrollFrame = null;
    this.autoplayTimer = null;

    if (!this.viewport || this.slides.length === 0) {
      return;
    }

    this.bindEvents();
    this.updateActive(0, false);
    this.startAutoplay();
  }

  bindEvents() {
    this.previousButton?.addEventListener('click', () => {
      this.goToPrevious();
      this.restartAutoplay();
    });

    this.nextButton?.addEventListener('click', () => {
      this.goToNext();
      this.restartAutoplay();
    });

    this.slides.forEach((slide, index) => {
      slide.addEventListener('click', (event) => {
        if (
          event.target.closest('a') &&
          index === this.activeIndex
        ) {
          return;
        }

        if (index !== this.activeIndex) {
          event.preventDefault();
          this.scrollToIndex(index);
          this.restartAutoplay();
        }
      });
    });

    this.viewport.addEventListener(
      'scroll',
      () => {
        window.cancelAnimationFrame(this.scrollFrame);

        this.scrollFrame = window.requestAnimationFrame(() => {
          this.detectCenteredSlide();
        });
      },
      {
        passive: true,
      }
    );

    this.section.addEventListener('mouseenter', () => {
      this.stopAutoplay();
    });

    this.section.addEventListener('mouseleave', () => {
      this.startAutoplay();
    });

    this.section.addEventListener('focusin', () => {
      this.stopAutoplay();
    });

    this.section.addEventListener('focusout', () => {
      this.startAutoplay();
    });

    document.addEventListener(
      'shopify:block:select',
      (event) => {
        const selectedSlide = this.section.querySelector(
          `[data-block-id="${event.detail.blockId}"]`
        );

        if (!selectedSlide) return;

        const index = this.slides.indexOf(selectedSlide);

        if (index >= 0) {
          this.scrollToIndex(index);
        }
      }
    );
  }

  detectCenteredSlide() {
    const viewportRect =
      this.viewport.getBoundingClientRect();

    const viewportCenter =
      viewportRect.left + viewportRect.width / 2;

    let closestIndex = this.activeIndex;
    let closestDistance = Number.POSITIVE_INFINITY;

    this.slides.forEach((slide, index) => {
      const slideRect = slide.getBoundingClientRect();
      const slideCenter =
        slideRect.left + slideRect.width / 2;
      const distance = Math.abs(
        viewportCenter - slideCenter
      );

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    if (closestIndex !== this.activeIndex) {
      this.updateActive(closestIndex, false);
    }
  }

  scrollToIndex(index, behavior = 'smooth') {
    const slide = this.slides[index];

    if (!slide) return;

    const viewportRect =
      this.viewport.getBoundingClientRect();

    const slideRect = slide.getBoundingClientRect();

    const targetLeft =
      this.viewport.scrollLeft +
      slideRect.left -
      viewportRect.left -
      (viewportRect.width - slideRect.width) / 2;

    this.viewport.scrollTo({
      left: targetLeft,
      behavior,
    });

    this.updateActive(index, false);
  }

  updateActive(index) {
    this.activeIndex = index;

    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === index;

      slide.classList.toggle('is-active', isActive);

      slide.setAttribute(
        'aria-current',
        String(isActive)
      );
    });

    this.captions.forEach((caption, captionIndex) => {
      const isActive = captionIndex === index;

      caption.hidden = !isActive;
      caption.classList.toggle('is-active', isActive);
    });

    if (this.currentCounter) {
      this.currentCounter.textContent = String(index + 1);
    }

    this.updateButtons();
  }

  updateButtons() {
    if (this.loop) {
      if (this.previousButton) {
        this.previousButton.disabled = false;
      }

      if (this.nextButton) {
        this.nextButton.disabled = false;
      }

      return;
    }

    if (this.previousButton) {
      this.previousButton.disabled =
        this.activeIndex === 0;
    }

    if (this.nextButton) {
      this.nextButton.disabled =
        this.activeIndex === this.slides.length - 1;
    }
  }

  goToPrevious() {
    let nextIndex = this.activeIndex - 1;

    if (nextIndex < 0) {
      nextIndex = this.loop
        ? this.slides.length - 1
        : 0;
    }

    this.scrollToIndex(nextIndex);
  }

  goToNext() {
    let nextIndex = this.activeIndex + 1;

    if (nextIndex >= this.slides.length) {
      nextIndex = this.loop
        ? 0
        : this.slides.length - 1;
    }

    this.scrollToIndex(nextIndex);
  }

  startAutoplay() {
    if (
      !this.autoplay ||
      this.slides.length < 2 ||
      window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches
    ) {
      return;
    }

    this.stopAutoplay();

    this.autoplayTimer = window.setInterval(() => {
      this.goToNext();
    }, this.interval);
  }

  stopAutoplay() {
    if (!this.autoplayTimer) return;

    window.clearInterval(this.autoplayTimer);
    this.autoplayTimer = null;
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }
}

function initializeCTFeedbackSpotlight(root = document) {
  root
    .querySelectorAll(
      '[data-ct-feedback-spotlight]:not([data-ct-feedback-spotlight-initialized])'
    )
    .forEach((section) => {
      section.setAttribute(
        'data-ct-feedback-spotlight-initialized',
        'true'
      );

      new CTFeedbackSpotlight(section);
    });
}

document.addEventListener('DOMContentLoaded', () => {
  initializeCTFeedbackSpotlight();
});

document.addEventListener(
  'shopify:section:load',
  (event) => {
    initializeCTFeedbackSpotlight(event.target);
  }
);